"use client";

import React from "react";
import { Check, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const plans = [{
  id: "standard",
  name: "Core",
  duration: "agent",
  price: "$49",
  description: "Essential AI support for growing technical teams.",
  benefits: ["Automated Ticket Routing", "Basic Case Resolution", "Standard API Access", "9/5 Email Support"],
  isPopular: false,
  buttonText: "Select Plan",
  link: "/ServiceTiersPage"
}, {
  id: "gold",
  name: "Enterprise",
  duration: "agent",
  price: "$149",
  description: "Full-scale autonomous support for complex architectures.",
  benefits: ["Advanced Case Solving", "Custom Neural Training", "Unlimited API Integration", "24/7 Priority Response", "Real-time Efficiency Metrics"],
  isPopular: true,
  buttonText: "Select Plan",
  link: "/ServiceTiersPage"
}, {
  id: "silver",
  name: "Global",
  duration: "agent",
  price: "$99",
  description: "High-performance support for distributed networks.",
  benefits: ["Multi-region Deployment", "Complex Logic Handling", "Full System Integration", "Priority Support Access", "Weekly Performance Audits"],
  isPopular: false,
  buttonText: "Select Plan",
  link: "/ServiceTiersPage"
}];
export default function InvestmentInAutonomy() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224297" id="investment-in-autonomy" ref={sectionRef} className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none opacity-10" style={{
      backgroundImage: 'radial-gradient(circle, var(--color-muted) 1px, transparent 1px)',
      backgroundSize: '40px 40px'
    }}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/20 blur-[120px] rounded-lg opacity-40" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[400px] bg-accent/10 blur-[100px] rounded-lg opacity-20" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-1000 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <h2 className="font-default text-4xl md:text-5xl font-bold mb-6 tracking-tight">
            Investment in <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-emerald-400">Autonomy</span>
          </h2>
          <p className="text-muted-foreground text-lg font-sans">
            Deploy high-performance AI agents that solve complex support cases at a fraction of the cost of traditional human teams.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 items-center max-w-6xl mx-auto">
          {plans.map((plan, i) => <div data-index={i} key={plan.id} className={`transition-all duration-700 delay-[${i * 150}ms] transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} ${plan.isPopular ? "md:order-2" : i === 0 ? "md:order-1" : "md:order-3"}`}>
              <Card className={`relative h-full flex flex-col bg-card/40 backdrop-blur-md border-border/20 transition-all duration-300 hover:shadow-lg ${plan.isPopular ? "md:scale-110 z-20 border-primary/50 shadow-[0_0_40px_-10px_rgba(0,255,255,0.2)]" : "z-10 hover:border-primary/30"}`}>
                {plan.isPopular && <div className="absolute -top-4 left-0 right-0 flex justify-center z-30">
                    <Badge className="bg-primary text-primary-foreground px-4 py-1 text-sm font-bold uppercase tracking-wider shadow-md flex items-center gap-1 rounded-lg">
                      <Sparkles className="w-4 h-4" />
                      Most Popular
                    </Badge>
                  </div>}

                <CardHeader className="text-center pb-2 pt-8">
                  <CardTitle className="font-default text-2xl text-muted-foreground mb-2">
                    {plan.name}
                  </CardTitle>
                  <div className="flex justify-center items-baseline gap-1 mb-2">
                    <span className="font-default text-5xl font-bold text-white drop-shadow-[0_4px_4px_rgba(0,0,0,0.5)]">
                      {plan.price}
                    </span>
                    <span className="text-muted-foreground text-sm font-mono uppercase tracking-widest">
                      / {plan.duration}
                    </span>
                  </div>
                  <CardDescription className="text-sm font-sans min-h-[40px] text-muted-foreground/80">
                    {plan.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="flex-grow pt-6">
                  <ul className="space-y-4">
                    {plan.benefits.map((benefit, index) => <li key={index} data-index={index} className="flex items-start gap-3 text-sm font-sans">
                        <div className="mt-0.5 bg-emerald-500/10 p-1 rounded-md shrink-0">
                          <Check className="w-4 h-4 text-emerald-500" />
                        </div>
                        <span className="text-foreground/90">{benefit}</span>
                      </li>)}
                  </ul>
                </CardContent>

                <CardFooter className="pt-6 pb-8">
                  <Button asChild size="lg" className={`w-full font-bold text-base transition-all duration-300 rounded-lg ${plan.isPopular ? "bg-primary hover:bg-primary/90 text-primary-foreground border-0 shadow-[0_0_20px_-5px_rgba(0,255,255,0.4)] relative overflow-hidden group" : "bg-transparent hover:bg-primary/10 text-foreground border border-border hover:border-primary/50"}`}>
                    <Link to={plan.link}>
                      {plan.isPopular && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out" />}
                      <span className="relative z-10">{plan.buttonText}</span>
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}